import { getLocale, t } from './i18n.mjs?v=20260806.100';

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function initials(value) {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length === 1 ? parts[0].slice(0, 2) : `${parts[0][0]}${parts.at(-1)[0]}`).toUpperCase();
}

function roleLabel(value, locale = getLocale()) {
  const role = String(value || 'user');
  return t(`settings.role.${role}`, {}, locale);
}

function referencePath(reference) {
  if (!reference) return '';
  if (typeof reference === 'string') return reference;
  return reference.path || reference.id || '';
}

const SETTINGS_ROLES = Object.freeze(['admin', 'dispatcher', 'clerk', 'finance', 'customer']);
const SETTINGS_CURRENT_ROLES = Object.freeze([...SETTINGS_ROLES, 'driver']);

export function settingsRoleChangeRequest({ uid, currentRole, nextRole } = {}) {
  const safeUid = String(uid || '').trim();
  const safeCurrentRole = String(currentRole || '').trim().toLowerCase();
  const safeNextRole = String(nextRole || '').trim().toLowerCase();
  if (!safeUid) throw new Error('USER_REQUIRED');
  if (!SETTINGS_CURRENT_ROLES.includes(safeCurrentRole) || !SETTINGS_ROLES.includes(safeNextRole)) {
    throw new Error('ROLE_NOT_ALLOWED');
  }
  if (safeCurrentRole === safeNextRole) throw new Error('ROLE_UNCHANGED');
  return Object.freeze({ uid: safeUid, currentRole: safeCurrentRole, nextRole: safeNextRole });
}

/**
 * The Drivers in the dispatch menu, managed here by Admin (Alan, 2026-10-17: "选项司机那一部分允许删一些然后加一些
 * 司机", "管理员自己管"). 停用 takes a Driver out of every driver menu - the menu lists active Drivers only - and keeps
 * everything they ever did; 启用 puts them back. 添加 makes the Driver and the Driver-app login in one press.
 */
export function settingsDriversMarkup({ drivers = [], locale = getLocale() } = {}) {
  const zh = String(locale).toLowerCase().startsWith('zh');
  const rows = (Array.isArray(drivers) ? drivers : []).map((driver) => {
    const active = driver?.active === true;
    const login = String(driver?.email || '').replace(/@tms\.com$/u, '');
    return `<article class="settings-user settings-driver${active ? '' : ' is-inactive'}" data-settings-driver="${escapeHtml(driver.id)}">
              <span class="settings-user__avatar" aria-hidden="true">${escapeHtml(initials(driver.full_name))}</span>
              <div class="settings-user__identity"><strong>${escapeHtml(driver.full_name || driver.id)}</strong>${login ? `<small>${escapeHtml(login)}${driver.tractor_no ? ` · ${escapeHtml(driver.tractor_no)}` : ''}</small>` : ''}</div>
              <span class="settings-user__state settings-user__state--${active ? 'active' : 'inactive'}"><i aria-hidden="true"></i>${escapeHtml(active ? (zh ? '在菜单里' : 'In the menu') : (zh ? '已停用' : 'Disabled'))}</span>
              <button type="button" class="settings-driver__toggle" data-driver-toggle="${escapeHtml(driver.id)}" data-driver-active="${active}">${escapeHtml(active ? (zh ? '停用' : 'Disable') : (zh ? '启用' : 'Enable'))}</button>
            </article>`;
  }).join('');
  return `<section class="settings-surface settings-drivers" aria-labelledby="settingsDriversTitle">
        <header class="settings-surface__header"><h2 id="settingsDriversTitle">${escapeHtml(zh ? '司机' : 'Drivers')}</h2></header>
        <form class="settings-driver-add" data-settings-driver-add novalidate>
          <label><span>${escapeHtml(zh ? '司机名字' : 'Name')}</span><input name="full_name" maxlength="200" autocomplete="off" required></label>
          <label><span>${escapeHtml(zh ? '登录名（字母和数字）' : 'Login name (letters and digits)')}</span><input name="login_name" maxlength="31" autocomplete="off" spellcheck="false" required></label>
          <label><span>${escapeHtml(zh ? '密码（8 位以上）' : 'Password (8+)')}</span><input name="password" type="password" maxlength="72" autocomplete="new-password" required></label>
          <label><span>${escapeHtml(zh ? '车头号（可空）' : 'Truck # (optional)')}</span><input name="tractor_no" maxlength="40" autocomplete="off"></label>
          <button type="submit">${escapeHtml(zh ? '添加司机' : 'Add Driver')}</button>
          <p class="settings-driver-add__error" data-settings-driver-error role="alert" hidden></p>
        </form>
        <div class="settings-user-list settings-driver-list">${rows}</div>
      </section>`;
}

/** Wires the Drivers section: the toggle and the add form. Both report through the handlers, then the page redraws. */
export function mountSettingsDrivers(root, {
  onToggle = async () => {}, onCreate = async () => {}, onDone = () => {}, onError = () => {},
} = {}) {
  const section = root?.querySelector?.('.settings-drivers');
  if (!section) return Object.freeze({ destroy() {} });
  const controller = new AbortController();
  const { signal } = controller;
  section.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-driver-toggle]');
    if (!button || button.disabled) return;
    button.disabled = true;
    try {
      await onToggle({ driverId: button.dataset.driverToggle, enable: button.dataset.driverActive !== 'true' });
      onDone();
    } catch (error) {
      button.disabled = false;
      onError(error);
    }
  }, { signal });
  const form = section.querySelector('[data-settings-driver-add]');
  const errorHost = section.querySelector('[data-settings-driver-error]');
  // Its own flag, not the button's disabled: the app-wide double-submit guard disables every submit button in the
  // capture phase, before this handler runs, so "already disabled" would read as "already sending" and drop it.
  let sending = false;
  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (sending) return;
    sending = true;
    const values = Object.fromEntries(new FormData(form).entries());
    if (errorHost) { errorHost.hidden = true; errorHost.textContent = ''; }
    try {
      await onCreate(values);
      onDone();
    } catch (error) {
      if (errorHost) { errorHost.hidden = false; errorHost.textContent = error?.message || String(error); }
    } finally {
      sending = false;
      const submit = form.querySelector('button[type="submit"]');
      if (submit) submit.disabled = false;
    }
  }, { signal });
  return Object.freeze({ destroy: () => controller.abort() });
}

export function settingsPageMarkup({ users = [], roles = [], drivers = null, locale = getLocale() } = {}) {
  const roleOptions = Array.isArray(roles) ? roles : [];
  return `<main class="settings-page">
    <div class="settings-page__layout">
      <section class="settings-surface settings-access" aria-labelledby="settingsAccessTitle">
        <header class="settings-surface__header">
          <h2 id="settingsAccessTitle">${escapeHtml(t('settings.accessRoles', {}, locale))}</h2>
          <div class="settings-access__tools">
            <label class="settings-user-search">
              <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
              <span class="settings-visually-hidden">${escapeHtml(t('settings.searchUsers', {}, locale))}</span>
              <input type="search" data-settings-user-search placeholder="${escapeHtml(t('settings.nameOrEmail', {}, locale))}" autocomplete="off">
            </label>
            <span class="settings-count-badge">${escapeHtml(t('settings.userCount', { count: users.length }, locale))}</span>
            ${/*
              The doors to Add Customer and Add Driver (Alan, 2026-08-21: "加上
              both driver 和 customer但是只能admin加"). He looked for Add Customer
              here first - "我在adminpage 根本没看见加顾客的选项" - and this is the
              page that holds accounts and roles, so it is where both belong.

              No role check in this markup, and that is not an oversight: the
              admin-users route is in Admin's list alone in web/role-policy.mjs,
              so this page cannot be opened by anyone else. The permission that
              refuses is the callables', narrowed to Admin in the same change.
            */ ''}
            <a class="settings-add-record" href="#/create/customer" data-add-customer>${escapeHtml(t('settings.addCustomer', {}, locale))}</a>
            <a class="settings-add-record" href="#/create/driver" data-add-driver>${escapeHtml(t('settings.addDriver', {}, locale))}</a>
          </div>
        </header>
        <div class="settings-user-list" data-settings-user-list>
          ${users.map((user) => {
    const name = user.email || user.display_name || user.id;
    const searchableName = user.display_name || name;
    const customerPath = referencePath(user.customer_ref);
    const active = user.active !== false;
    const search = `${searchableName} ${user.email || ''} ${user.role || ''}`.toLowerCase();
    return `<article class="settings-user" data-settings-user data-settings-search="${escapeHtml(search)}">
              <span class="settings-user__avatar" aria-hidden="true">${escapeHtml(initials(name))}</span>
              <div class="settings-user__identity"><strong title="${escapeHtml(name)}">${escapeHtml(name)}</strong>${customerPath ? `<small>${escapeHtml(t('settings.accountLink', { path: customerPath }, locale))}</small>` : ''}</div>
              <span class="settings-user__state settings-user__state--${active ? 'active' : 'inactive'}"><i aria-hidden="true"></i>${escapeHtml(t(active ? 'settings.active' : 'settings.inactive', {}, locale))}</span>
              <label class="settings-user__role"><span class="settings-visually-hidden">${escapeHtml(t('settings.role', {}, locale))}</span><select data-role-uid="${escapeHtml(user.id)}" data-user-name="${escapeHtml(name)}" data-current-role="${escapeHtml(user.role)}" aria-label="${escapeHtml(t('settings.roleFor', { name }, locale))}">
                ${user.role === 'driver' ? `<option value="driver" selected disabled>${escapeHtml(t('settings.driverRetired', {}, locale))}</option>` : ''}
                ${roleOptions.map((role) => `<option value="${escapeHtml(role)}" ${role === user.role ? 'selected' : ''}>${escapeHtml(roleLabel(role, locale))}</option>`).join('')}
              </select></label>
            </article>`;
  }).join('')}
        </div>
        <p class="settings-empty" data-settings-empty hidden>${escapeHtml(t('settings.noMatches', {}, locale))}</p>
      </section>
      ${Array.isArray(drivers) ? settingsDriversMarkup({ drivers, locale }) : ''}
      <section class="settings-surface settings-audit" aria-labelledby="settingsAuditTitle">
        <header class="settings-surface__header"><h2 id="settingsAuditTitle">${escapeHtml(t('settings.auditTitle', {}, locale))}</h2></header>
        <div id="adminAuditLog"></div>
      </section>
    </div>
    <section class="settings-role-confirmation" data-settings-role-confirmation role="dialog" aria-modal="false" aria-labelledby="settingsRoleConfirmTitle" hidden>
      <span class="settings-role-confirmation__arrow" aria-hidden="true"></span>
      <small>${escapeHtml(t('settings.accessChange', {}, locale))}</small>
      <h2 id="settingsRoleConfirmTitle">${escapeHtml(t('settings.confirmRole', {}, locale))}</h2>
      <p data-settings-role-summary></p>
      <p class="settings-role-confirmation__guidance">${escapeHtml(t('settings.guidance', {}, locale))}</p>
      <p class="settings-role-confirmation__error" data-settings-role-error role="alert" hidden></p>
      <div class="settings-role-confirmation__actions">
        <button type="button" data-settings-role-cancel>${escapeHtml(t('settings.cancel', {}, locale))}</button>
        <button type="button" data-settings-role-confirm>${escapeHtml(t('settings.update', {}, locale))}</button>
      </div>
    </section>
  </main>`;
}

export function mountSettingsUserSearch(root) {
  const input = root?.querySelector?.('[data-settings-user-search]');
  const rows = [...(root?.querySelectorAll?.('[data-settings-user]') || [])];
  const empty = root?.querySelector?.('[data-settings-empty]');
  if (!input) return Object.freeze({ destroy() {} });
  const onInput = () => {
    const query = input.value.trim().toLowerCase();
    let visible = 0;
    rows.forEach((row) => {
      const match = !query || String(row.dataset.settingsSearch || '').includes(query);
      row.hidden = !match;
      if (match) visible += 1;
    });
    if (empty) empty.hidden = visible !== 0;
  };
  input.addEventListener('input', onInput);
  return Object.freeze({ destroy: () => input.removeEventListener('input', onInput) });
}

export function mountSettingsRoleChanges(root, {
  onConfirm = async () => {},
  onSuccess = () => {},
  onError = () => {},
} = {}) {
  const page = root?.querySelector?.('.settings-page');
  const panel = page?.querySelector?.('[data-settings-role-confirmation]');
  const selects = [...(page?.querySelectorAll?.('[data-role-uid]') || [])];
  if (!page || !panel || !selects.length) return Object.freeze({ destroy() {} });
  const doc = page.ownerDocument || globalThis.document;
  const summary = panel.querySelector('[data-settings-role-summary]');
  const errorHost = panel.querySelector('[data-settings-role-error]');
  const confirmButton = panel.querySelector('[data-settings-role-confirm]');
  const cancelButton = panel.querySelector('[data-settings-role-cancel]');
  const controller = new AbortController();
  const { signal } = controller;
  let activeSelect = null;
  let request = null;

  const positionPanel = () => {
    if (!activeSelect || panel.hidden) return;
    const rect = activeSelect.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const viewportWidth = doc?.documentElement?.clientWidth || globalThis.innerWidth || 1024;
    const viewportHeight = doc?.documentElement?.clientHeight || globalThis.innerHeight || 768;
    const left = Math.max(10, Math.min(rect.right - panelRect.width, viewportWidth - panelRect.width - 10));
    const below = rect.bottom + 9;
    const top = below + panelRect.height <= viewportHeight - 10
      ? below
      : Math.max(10, rect.top - panelRect.height - 9);
    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
    panel.classList.toggle('is-above', top < rect.top);
    panel.style.setProperty('--settings-role-arrow-left', `${Math.round(Math.max(16, Math.min(panelRect.width - 22, rect.left + rect.width / 2 - left)))}px`);
  };

  const close = ({ restore = true } = {}) => {
    if (restore && activeSelect && request) activeSelect.value = request.currentRole;
    if (activeSelect) activeSelect.disabled = false;
    panel.hidden = true;
    panel.removeAttribute('aria-busy');
    errorHost.hidden = true;
    errorHost.textContent = '';
    confirmButton.disabled = false;
    cancelButton.disabled = false;
    activeSelect = null;
    request = null;
  };

  const open = (select) => {
    try {
      request = settingsRoleChangeRequest({
        uid: select.dataset.roleUid,
        currentRole: select.dataset.currentRole,
        nextRole: select.value,
      });
    } catch (error) {
      if (error?.message === 'ROLE_UNCHANGED') return;
      select.value = select.dataset.currentRole;
      onError(error);
      return;
    }
    activeSelect = select;
    const locale = getLocale();
    summary.textContent = t('settings.roleChangeSummary', {
      name: select.dataset.userName || t('settings.thisUser', {}, locale),
      from: roleLabel(request.currentRole, locale),
      to: roleLabel(request.nextRole, locale),
    }, locale);
    panel.hidden = false;
    positionPanel();
    confirmButton.focus();
  };

  selects.forEach((select) => select.addEventListener('change', () => open(select), { signal }));
  cancelButton.addEventListener('click', () => close(), { signal });
  confirmButton.addEventListener('click', async () => {
    if (!activeSelect || !request) return;
    panel.setAttribute('aria-busy', 'true');
    confirmButton.disabled = true;
    cancelButton.disabled = true;
    activeSelect.disabled = true;
    try {
      await onConfirm(request);
      activeSelect.dataset.currentRole = request.nextRole;
      close({ restore: false });
      onSuccess(request);
    } catch (error) {
      panel.removeAttribute('aria-busy');
      confirmButton.disabled = false;
      cancelButton.disabled = false;
      activeSelect.disabled = false;
      errorHost.textContent = error?.message || t('settings.roleUpdateFailed');
      errorHost.hidden = false;
      onError(error);
    }
  }, { signal });
  doc?.addEventListener?.('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) close();
  }, { signal });
  globalThis.addEventListener?.('resize', positionPanel, { signal });
  globalThis.addEventListener?.('scroll', positionPanel, { signal, capture: true });

  return Object.freeze({
    destroy() {
      close();
      controller.abort();
    },
  });
}
